<template lang="pug">
  //- Gallery (build brief section 7): curated, image-only square grid, photographs fitted uncropped
  section.gallery.pt-72.md-pt-144.pb-72(:class="{'gallery--teaser': teaser}")
    .gallery__grid(v-if="items.length")
      router-link.gallery__cell.block(v-for="(prj, i) in items", :key="prj.uid", :to="{name: 'project', params: {uid: prj.uid}}", :class="cellClass(prj)", :style="{'--ar': ar(prj)}", :aria-label="captionText(prj)")
        figure.gallery__frame.m-0
          .gallery__img
            img.gallery__pic(v-if="i < eager", key="eager", :src="src(prj)", :alt="alt(prj)", draggable="false")
            img.gallery__pic(v-else, key="lazy", v-lazy="src(prj)", :alt="alt(prj)", draggable="false")
          figcaption.gallery__caption.type-caption.tracking-40
            i {{ txt(prj.data.title) }}
            | , {{ txt(prj.data.location) }}, {{ prjYear(prj) }}
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import { prjYear, imgixWidth } from '@/utils'
import _get from 'lodash/get'

export default {
  name: 'ProjectsByGallery',
  props: {
    fetch: { type: Boolean, default: false },
    teaser: { type: Boolean, default: false } // end-of-project preview: first two rows only
  },
  data () {
    return {
      txt: this.$prismic.richTextAsPlain,
      prjYear,
      eager: 6 // images above the fold load straight away, the rest lazy load (H2)
    }
  },
  computed: {
    ...mapState(['winW', 'projects']),
    /**
     * C1: only projects ticked "Show in Gallery", ordered by "Gallery order" (1 = first), ties by mural date (newest first).
     * Until at least one project is ticked (or before the field exists), every project shows, so the
     * page is never blank while the curation is still being done in Prismic.
     */
    items () {
      const all = this.projects.filter(prj => prj && prj.data && _get(prj, 'data.index_image.url'))
      const ticked = all.filter(prj => prj.data.show_in_gallery === true)
      const list = ticked.length ? ticked : all
      const order = prj => {
        const n = parseFloat(prj.data.gallery_order)
        return isNaN(n) ? Number.POSITIVE_INFINITY : n
      }
      const date = prj => new Date(prj.data.mural_completion_date || 0).getTime()
      const sorted = list.slice().sort((a, b) => (order(a) - order(b)) || (date(b) - date(a)))
      return this.teaser ? sorted.slice(0, 6) : sorted
    },
    // rendered cell width, for right-sized images from the Prismic CDN
    cellW () {
      const w = this.winW
      const bk = Vue.config.bkpts
      const gap = 24
      if (w >= bk.lg) return Math.round((w * 10 / 18 - 2 * gap) / 3)
      if (w >= bk.md) return Math.round((w * 16 / 18 - 2 * gap) / 3)
      return w - 48
    }
  },
  methods: {
    ar (prj) {
      const d = _get(prj, 'data.index_image.dimensions')
      return d && d.height ? +(d.width / d.height).toFixed(4) : 1.5
    },
    fill (prj) {
      return /fill/i.test(prj.data.thumbnail_fit || '')
    },
    cellClass (prj) {
      const ar = this.ar(prj)
      return {
        'gallery__cell--fill': this.fill(prj),
        'gallery__cell--portrait': !this.fill(prj) && ar < 1,
        'gallery__cell--landscape': !this.fill(prj) && ar >= 1 && ar > 1.02,
        'gallery__cell--square': !this.fill(prj) && ar >= 1 && ar <= 1.02
      }
    },
    src (prj) {
      // the main (uncropped) index image, sized to the cell, never the 4:5 / 1:1 / 4:3 crops
      return imgixWidth(prj.data.index_image.url, this.cellW)
    },
    alt (prj) {
      return prj.data.index_image.alt || this.captionText(prj)
    },
    captionText (prj) {
      return `${this.txt(prj.data.title)}, ${this.txt(prj.data.location)}, ${prjYear(prj)}`
    }
  },
  async mounted () {
    if (this.fetch) await this.$store.dispatch('getProjects')
  }
}
</script>

<style>
/* --- grid geometry (C2): three equal squares per row inside the page margin, one column on phones --- */
.gallery {
  --gallery-inset: 2.4rem;
  --gallery-gap: 2.4rem;
  padding-left: var(--gallery-inset);
  padding-right: var(--gallery-inset);
}
@media (min-width: 768px) {
  .gallery { --gallery-inset: calc(100% * 1 / 18); }
}
@media (min-width: 1024px) {
  /* matches the Archive list's left offset (the 4/18 hover-image column) */
  .gallery { --gallery-inset: calc(100% * 4 / 18); }
}
.gallery__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--gallery-gap);
}
.gallery__cell {
  position: relative;
  color: black;
  cursor: pointer;
  -webkit-user-drag: none;
}
.gallery__frame {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* the square */
}

/* --- fit content to frame (C3): the picture keeps its own ratio inside the square --- */
.gallery__img {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  background: #f9f9f9;
}
.gallery__cell--landscape .gallery__img { width: 100%; height: 0; padding-bottom: calc(100% / var(--ar)); }
.gallery__cell--portrait .gallery__img { height: 100%; width: calc(var(--ar) * 100%); }
.gallery__cell--square .gallery__img,
.gallery__cell--fill .gallery__img { width: 100%; height: 100%; }
.gallery__pic {
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  transition: transform 500ms ease, opacity 300ms;
}
.gallery__cell--fill .gallery__pic { object-fit: cover; }
.gallery__pic[lazy="loading"] { opacity: 0; }
.gallery__pic[lazy="loaded"] { opacity: 1; }

/* --- captions (C4): anchored to the bottom edge of the picture, below it when there is room, over it when not --- */
.gallery__caption {
  position: absolute;
  left: 0;
  right: 0;
  margin: 0;
  line-height: 1.3;
  pointer-events: none;
}
/* landscape: black, directly under the image, left indent 8px */
.gallery__cell--landscape .gallery__caption {
  top: calc(50% + 50% / var(--ar));
  padding: 0.4rem 0 0 8px;
  color: black;
}
/* portrait, square and fill: white over the bottom of the photo on a soft dark gradient */
.gallery__cell--portrait .gallery__caption,
.gallery__cell--square .gallery__caption,
.gallery__cell--fill .gallery__caption {
  bottom: 0;
  padding: 3.2rem 8px 0.8rem;
  color: white;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0) 100%);
}
.gallery__cell--portrait .gallery__caption {
  left: calc(50% - var(--ar) * 50%);
  right: calc(50% - var(--ar) * 50%);
}

/* desktop hover: nothing at rest, slight zoom plus the caption on the hovered image only */
@media (hover: hover) and (min-width: 768px) {
  .gallery__caption { opacity: 0; transition: opacity 250ms ease; }
  .gallery__cell:hover .gallery__caption,
  .gallery__cell:focus .gallery__caption { opacity: 1; }
  .gallery__cell:hover .gallery__pic { transform: scale(1.03); }
}

/* --- mobile (C5): one column, natural ratio, caption always visible and tight under the photo --- */
@media (max-width: 767px) {
  .gallery__grid { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .gallery__cell { margin-bottom: 3.6rem; }
  .gallery__frame { padding-bottom: calc(100% / var(--ar)); }
  .gallery__img,
  .gallery__cell--landscape .gallery__img,
  .gallery__cell--portrait .gallery__img {
    left: 0; top: 0; width: 100%; height: 100%; padding: 0; transform: none;
  }
  .gallery__cell .gallery__caption {
    top: 100%; bottom: auto; left: 0; right: 0;
    padding: 0.4rem 0 0;
    color: black;
    background: none;
    opacity: 1;
  }
}

/* end-of-project teaser keeps the same grid but no top spacing of its own */
.gallery--teaser { padding-bottom: 0; }
</style>
